import { createFileRoute, Link } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { PASSAGES, passagePath } from "../lib/passage";
import {
  breadcrumbJsonLd,
  canonical,
  collectionJsonLd,
  documentHeaders,
  socialMeta,
} from "../lib/seo";
import { VERSE_TOPICS, verseMinutes, versePath } from "../lib/verses";

const TITLE = "Bible verses by topic — Bible Shield";
const DESCRIPTION =
  "Short collections of scripture on self-control, night anxiety, rest, comparison, time and temptation — ten passages each, with a note on what every one actually says.";

/**
 * `/verses` — the hub.
 *
 * ── Why these six topics and not the obvious ones ────────────────────────────
 *
 * "Bible verses about love" is searched enormously and is owned by sites with
 * twenty years of authority and the whole text of scripture behind them. There
 * is no version of competing there that is worth anyone's afternoon.
 *
 * These six are the ones this site has something to say about that a generic
 * verse index does not: attention, restraint, the hour before bed, envy from a
 * feed, and what to do at the moment of reaching. They are also the topical
 * neighbourhood of everything else here, which is what makes them worth having
 * — a cluster of related pages is understood as expertise in a subject, while
 * six unrelated pages are understood as six pages.
 */
export const Route = createFileRoute("/verses/")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/verses",
        lang: "en",
      }),
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "Verses", path: "/verses" },
        ]),
      },
      {
        "script:ld+json": collectionJsonLd({
          name: TITLE,
          description: DESCRIPTION,
          path: "/verses",
          items: VERSE_TOPICS.map((t) => ({
            name: t.h1,
            path: versePath(t.slug),
          })),
        }),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/verses") }],
  }),
  component: VersesIndex,
});

function VersesIndex() {
  return (
    <PageChrome>
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        scripture
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        Verses by topic
      </h1>
      <p className="mt-4 max-w-[60ch] text-lg leading-relaxed text-ink2">
        Ten passages each, quoted in full from the World English Bible, with a
        note on what every one of them actually says — including the famous ones
        that do not mean what they are usually quoted to mean.
      </p>

      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        {VERSE_TOPICS.map((topic) => (
          <li key={topic.slug}>
            <Link
              to="/verses/$slug"
              params={{ slug: topic.slug }}
              className="brutal flex h-full flex-col gap-3 p-6 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {topic.verses.length} passages · {verseMinutes(topic)} min
              </span>
              <span className="font-display text-xl leading-snug font-bold text-ink">
                {topic.h1}
              </span>
              <span className="leading-relaxed text-body">
                {topic.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-14 border-t-[2.5px] border-ink pt-10">
        <h2 className="font-display text-2xl font-bold text-ink">
          Or one passage in depth
        </h2>
        <p className="mt-3 max-w-[60ch] leading-relaxed text-ink2">
          The eight most-read passages in English Christianity, each with its
          context printed and the popular misreading answered.
        </p>
        <ul className="mt-5 flex flex-wrap gap-3">
          {PASSAGES.map((passage) => (
            <li key={passage.slug}>
              <a
                href={passagePath(passage.slug)}
                className="inline-flex rounded-pill border-[2.5px] border-ink bg-surface px-4 py-2 font-display text-sm font-bold text-ink transition-colors hover:bg-brand-wash"
              >
                {passage.reference}
              </a>
            </li>
          ))}
        </ul>
      </section>

      <p className="mt-10 leading-relaxed text-ink2">
        Quotations are from the World English Bible, which is in the public
        domain — which is why these pages can print a passage whole rather than
        gesturing at it.
      </p>
    </PageChrome>
  );
}
